/**
 * SalonHub メインアプリケーション
 * HITOYASUMIプロダクション対応 - 認証統合版
 * 
 * @description 認証状態に基づくメインコンテンツ切り替え
 * @authentication JWT + 暗号化Token管理
 * @security 業界最高水準セキュリティ
 */

import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './AppRouter';
import SessionManager from './components/auth/SessionManager';
import { useStore } from './store/useStore';
import { AuthProvider, useAuthContext } from './hooks/useAuth';
import { PermissionProvider } from './hooks/usePermissions';
import { ErrorProvider } from './contexts/ErrorContext';
import theme from './theme';
import './styles/globals.css';
import { checkAndMigrateData } from './utils/dataMigration';
import { InitialDataService } from './services/initialDataService';
import { DataService } from './services/dataService';
import logger from './utils/logger';

/**
 * 認証状態に基づくメインコンテンツ表示
 */
const AppContent: React.FC = () => {
  const { isAuthenticated, isInitializing, user } = useAuthContext();
  const { loadCustomers, loadReservations } = useStore();
  
  // 統合されたデータ初期化（認証成功後に実行）
  useEffect(() => {
    if (!isAuthenticated) return;

    const initializeAppData = async () => {
      try {
        // DataService初期化（Supabase状態を考慮した自動フォールバック）
        DataService.initialize();
        
        // 統一フラグで初期化済みかチェック
        const isInitialized = localStorage.getItem('SALONHUB_COMPLETE_SETUP_v2');
        
        if (isInitialized !== 'true') {
          // SalonHubデータ初期化処理（本番環境では詳細ログを無効化）
          
          // データ移行処理（必要な場合のみ）
          await checkAndMigrateData();
          
          // 初期デモデータのセットアップ
          await InitialDataService.setupInitialData();
          
          // 統一完了フラグを設定
          localStorage.setItem('SALONHUB_COMPLETE_SETUP_v2', 'true');
          // SalonHubデータ初期化完了（本番環境では詳細ログを無効化）
        } else {
          // SalonHubデータ初期化済み（本番環境では詳細ログを無効化）
        }
        
        // 初期化完了後にstoreのデータを非同期で読み込み (UI保護)
        // ストアデータ読み込み処理（本番環境では詳細ログを無効化）
        const loadResults = await Promise.allSettled([
          loadCustomers(),
          loadReservations()
        ]);
        
        if (import.meta.env.DEV) {
          loadResults.forEach((result, index) => {
            const dataType = index === 0 ? 'customers' : 'reservations';
            if (result.status === 'rejected') {
              logger.error(`❌ Failed to load ${dataType}:`, result.reason);
            } else {
              logger.log(`✅ ${dataType} loaded successfully`);
            }
          });
        }
        
        // 開発環境でInitialDataServiceをグローバルに利用可能にする
        if (import.meta.env.DEV) {
          (window as typeof window & { InitialDataService: typeof InitialDataService }).InitialDataService = InitialDataService;
          
          // 開発用: LocalStorageリセット機能
          (window as typeof window & { resetDemoData: () => void }).resetDemoData = () => {
            InitialDataService.resetDemoData();
            localStorage.removeItem('hasInitialData');
            localStorage.removeItem('SALONHUB_COMPLETE_SETUP_v2');
            window.location.reload();
          };
          logger.log('📱 開発用コマンド: window.resetDemoData() でデモデータをリセット');
          // ユーザー情報は本番環境では非表示（セキュリティ保護）
        }
      } catch (error) {
        logger.error('❌ App data initialization error:', error);
        // エラーが発生してもアプリケーションは続行 - データ読み込みを非同期で試行
        logger.log('🔄 Attempting fallback data loading...');
        try {
          const fallbackResults = await Promise.allSettled([
            loadCustomers(),
            loadReservations()
          ]);
          
          fallbackResults.forEach((result, index) => {
            const dataType = index === 0 ? 'customers' : 'reservations';
            if (result.status === 'rejected') {
              logger.error(`❌ Fallback ${dataType} loading failed:`, result.reason);
            } else {
              logger.log(`✅ Fallback ${dataType} loading succeeded`);
            }
          });
        } catch (fallbackError) {
          logger.error('❌ Fallback data loading also failed:', fallbackError);
          // 最終的にエラーでもアプリケーションは続行
        }
      }
    };

    initializeAppData();
  }, [isAuthenticated, loadCustomers, loadReservations, user]);

  // 初回認証チェック中の表示
  if (isInitializing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(44, 85, 48, 0.05) 0%, rgba(139, 195, 74, 0.05) 50%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: '#2c5530',
            mb: 2,
          }}
        />
        <Box sx={{ textAlign: 'center' }}>
          <Box
            component="img"
            src="/logo.svg"
            alt="SalonHub Logo"
            sx={{
              height: 100,
              width: 'auto',
              opacity: 0.8,
              mb: 2,
            }}
          />
        </Box>
      </Box>
    );
  }

  // 認証状態に基づく表示切り替え（構造を統一）
  // 注: エラー表示は以下で処理:
  //   - 認証ページ(Login/Register): ページ内でインライン表示
  //   - 認証済みページ: SessionManagerで表示
  return (
    <>
      <SessionManager />
      <PermissionProvider>
        <AppRouter />
      </PermissionProvider>
    </>
  );
};

/**
 * メインAppコンポーネント
 */
const App: React.FC = () => {
  return (
    <ErrorProvider>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}>
            <AppContent />
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </ErrorProvider>
  );
};

export default App;